import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeftLong } from "react-icons/fa6";
import { IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import ReusableMuiTable from '../../../components/common/Table/Table';
import Button from '../../../components/common/Button/Button';
import { showToast } from '../../../components/common/ToastNotification/ToastNotification';
import { getFeaturesId, deleteFeatureId } from '../../../api/service/custompage.service';

const FeaturesList = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);


  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);


  const fetchFeatures = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getFeaturesId(id);

      console.log("API Response:", response);


      if (response?.data?.data && Array.isArray(response.data.data)) {
        setFeatures(response.data.data);
      } else if (response?.data && Array.isArray(response.data)) {
        setFeatures(response.data);
      } else if (Array.isArray(response)) {
        setFeatures(response);
      } else {
        console.log("No valid data found in response");
        setFeatures([]);
      }
    } catch (error) {
      console.error("Error fetching features:", error);
      showToast.error("Failed to fetch features");
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchFeatures();
    }
  }, [fetchFeatures, id]);


  const handleDelete = (featureId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This feature will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteFeatureId(featureId);
          showToast.success("Feature deleted successfully");
          fetchFeatures();
        } catch (error) {
          console.error("Delete error:", error);
          showToast.error("Failed to delete feature");
        }
      }
    });
  };


  const columns = [
    {
      name: "title",
      label: "Title",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const feature = features[tableMeta.rowIndex];
          return feature?.name || "N/A";
        },
      },
    },
    {
      name: "description",
      label: "Description",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          return value ? (
            <div style={{
              maxWidth: "300px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}>
              {value}
            </div>
          ) : "N/A";
        },
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const feature = features[tableMeta.rowIndex];
          return (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => navigate(`/updatecustompage-feature/${feature.id}`)}
                  sx={{
                    color: "#C39B22",
                    "&:hover": { backgroundColor: "rgba(195, 155, 34, 0.1)" }
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  onClick={() => handleDelete(feature.id)}
                  sx={{
                    color: "#d32f2f",
                    "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.1)" }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    },
  ];

  return (
    <>
      <div className="d-flex gap-2 align-items-center justify-content-between mb-3">
        <div className="d-flex gap-2 align-items-center">
          <FaArrowLeftLong
            style={{ cursor: "pointer" }}
            onClick={() => navigate(-1)}
          />
          <h3 className="management-tile mb-0 ml-2">Features List</h3>
        </div>

        <Button
          onClick={() => navigate(`/add-feature/${id}`)}
          style={{
            backgroundColor: "#C39B22",
            color: "#ffffff",
          }}
        >
          Add Feature
        </Button>
      </div>

      <ReusableMuiTable
        title=""
        data={features}
        columns={columns}
        loading={loading}
        options={{
          search: true,
          searchOpen: true,
          searchPlaceholder: "Search features...",
          selectableRows: "none",
          page: page - 1,
          rowsPerPage: 10,
          rowsPerPageOptions: [10, 25, 50, 100],
          onChangePage: (currentPage) => setPage(currentPage + 1),
          textLabels: {
            body: {
              noMatch: loading ? "Loading..." : "No features found",
            },
          },
        }}
      />
    </>
  );
};

export default FeaturesList;